import React from 'react';
import { Text, Stack, Container, Message } from 'basis';
import {
  NZ_DRIVERS_LICENCE,
  NZ_PASSPORT,
  NZ_BIRTH_CERTIFICATE,
  NZ_CITIZENSHIP,
  RELATED_PASSPORT,
  OTHER_PASSPORT,
  NZ_FIREARM_LICENSE,
  NONE,
} from 'store';
import { initFormConfig } from 'utils';
import { numbersOnlyPattern } from '_config';
import { AU_PASSPORT } from '_config/au/constants';
import { validLicenceNumberPattern, validResidencyNumberLetterPattern, countryOptions } from '_config/_constants';
import nzPassportImg from 'images/nz-passport-guided.png';
import compact from 'lodash/compact';
import { YellowMessage } from 'components/message/YellowMessage';

const timezoneOffsetInMs = new Date().getTimezoneOffset() * 60000;
const tomorrowEpoch = new Date().setDate(new Date().getDate() + 1);
const tomorrowLocalDate = new Date(tomorrowEpoch - timezoneOffsetInMs).toISOString().split('T')[0];
export const minExpiryDate = tomorrowLocalDate;
const nzPassportNumberPattern = '^[a-zA-Z0-9]*$';
const auPassportNumberPattern = '^[A-Z]{1,2}\\d{7}$';

export const nzInitialValues = {
  idType: '',
  licenceNumber: '',
  version: '',
  idNumber: '',
  expiryDate: {
    day: '',
    month: '',
    year: '',
  },
  name: '',
  medicareCardNumber: '',
  individualReferenceNumber: '',
  medicareCardColour: '',
  expiryDateGreen: {
    day: '',
    month: '',
    year: '',
  },
  expiryDateNonGreen: {
    day: '',
    month: '',
    year: '',
  },
  nameOnPassport: '',
  issuingCountry: '',
  visaStartDate: {
    day: '',
    month: '',
    year: '',
  },
  visaEndDate: {
    day: '',
    month: '',
    year: '',
  },
};

const getIdNumberFieldLabel = values => {
  switch (values.idType) {
    case AU_PASSPORT:
      return 'Australian Passport number';
    case NZ_BIRTH_CERTIFICATE:
      return 'Registration number';
    case NZ_CITIZENSHIP:
      return 'Certificate number';
    default:
      return 'ID number';
  }
};

const hasNzIdvAttempts = (values, store) => {
  if (store.hasUpfrontDriversLicence) {
    return Object.entries(store.idvAttempts || {})
      .filter(([key, value]) => key !== 'NZDriversLicence')
      .find(([key, value]) => value === true);
  }
  return Object.entries(store.idvAttempts || {}).find(([key, value]) => value === true);
};

const hasNoNzIdvAttempts = (values, store) => !hasNzIdvAttempts(values, store);

export const getFormConfig = storeState => {
  const { firstName, middleName, lastName } = storeState.acquisition.personalDetails;
  const fullName = compact([firstName, middleName, lastName]).join(' ');

  const idTypeOptions = [
    ...(!storeState.idvAttempts?.NZDriversLicence
      ? [
          {
            value: NZ_DRIVERS_LICENCE,
            label: 'New Zealand Drivers Licence',
          },
        ]
      : []),
    ...(!storeState.idvAttempts?.NZPassport
      ? [
          {
            value: NZ_PASSPORT,
            label: 'New Zealand Passport',
          },
        ]
      : []),
    ...(!storeState.idvAttempts?.AUPassport
      ? [
          {
            value: AU_PASSPORT,
            label: 'Australian Passport',
          },
        ]
      : []),
    ...(!storeState.idvAttempts?.NZBirthCertificate
      ? [
          {
            value: NZ_BIRTH_CERTIFICATE,
            label: 'New Zealand Birth Certificate',
          },
        ]
      : []),
    ...(!storeState.idvAttempts?.NZCitizenship
      ? [
          {
            value: NZ_CITIZENSHIP,
            label: 'New Zealand Citizenship Certificate',
          },
        ]
      : []),
    { value: 'NONE', label: 'None' },
  ];

  return initFormConfig([
    {
      condition: hasNzIdvAttempts,
      component: 'CustomComponent',
      testId: 'eidv-failure-message',
      children: () => (
        <YellowMessage>
          <Message
            testId="yellow-message"
            severity="warning-or-significant"
            title="Unfortunately, we weren't able to verify your identity with the document you just supplied."
          >
            Please supply us with another form of ID from the option below. If you currently don't hold any valid ID
            documents, you can skip this process by selecting 'None'.
          </Message>
        </YellowMessage>
      ),
    },
    {
      condition: hasNoNzIdvAttempts,
      component: 'InfoBlock',
      name: 'idv-intro',
      children: (
        <Container padding="2 0 0 0">
          <Stack gap="3">
            <Text>To assist you further, we will attempt to verify you with a valid ID document.</Text>{' '}
            <Text>If you don’t hold a valid ID document, you can skip this process by selecting ‘None’.</Text>
          </Stack>
        </Container>
      ),
    },
    {
      component: 'Select',
      name: 'idType',
      label: 'ID document',
      options: idTypeOptions,
      testId: 'id-type',
      onChange: (selectedValue, formData) => {
        const nextIdType = selectedValue?.selectedOption?.value ?? selectedValue?.value ?? selectedValue;
        formData.setValues(() => ({
          ...nzInitialValues,
          idType: nextIdType,
        }));
      },
    },
    {
      component: 'CustomComponent',
      testId: 'id-type-help-text',
      children: ({ formData }) => {
        return (
          formData?.state?.values?.idType === AU_PASSPORT && (
            <Text textStyle="body2" margin="-6 0 0 0">
              Australian Passports must not be expired more than 2 years.
              <br />
            </Text>
          )
        );
      },
    },
    {
      condition: values => values.idType === NZ_PASSPORT,
      component: 'CustomComponent',
      testId: 'nz-passport-sample-image',
      children: () => <img key={nzPassportImg} src={nzPassportImg} alt="New Zealand Passport" width="100%" />,
    },
    {
      condition: values => values.idType === NZ_DRIVERS_LICENCE || values.idType === NZ_FIREARM_LICENSE,
      component: 'Input',
      name: 'licenceNumber',
      label: 'Licence number',
      testId: 'licence-number',
      validate: {
        props: { fixedLength: 8, validPattern: validLicenceNumberPattern },
        messages: {
          required: 'Please enter your licence number.',
          fixedLength: 'Must have {{fixedLength}} characters.',
          invalid: 'Invalid Licence Number.',
        },
      },
      helpText: values =>
        values.idType === NZ_DRIVERS_LICENCE &&
        'Licence number is located on the front of the card, under the heading "Licence No".',
    },
    {
      condition: values => values.idType === NZ_DRIVERS_LICENCE,
      component: 'Input',
      name: 'version',
      label: 'Version',
      testId: 'version',
      validate: {
        props: { fixedLength: 3, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter the 3 digit version number.',
          fixedLength: 'Must have {{fixedLength}} characters.',
          invalid: 'Invalid Licence Version.',
        },
      },
      helpText: 'Version number is located on the front of the card, under the heading "Version".',
    },
    {
      condition: values => values.idType === AU_PASSPORT,
      component: 'InfoBlock',
      name: 'intro',
      children: (
        <Text>
          <Text as="p">
            <strong>Name on Passport:</strong>
          </Text>{' '}
          {fullName}
        </Text>
      ),
    },
    {
      condition: values => values.idType === NZ_PASSPORT,
      component: 'Input',
      name: 'idNumber',
      label: getIdNumberFieldLabel,
      testId: 'id-number',
      validate: {
        props: { minLength: 5, maxLength: 9, validPattern: nzPassportNumberPattern },
        messages: {
          required: 'Please enter your passport number.',
          minLength: 'Must have at least {{minLength}} characters.',
          maxLength: 'Must have at most {{maxLength}} characters.',
          invalid: 'Please enter letters and numbers only.',
        },
      },
    },
    {
      condition: values => values.idType === AU_PASSPORT,
      component: 'Input',
      name: 'idNumber',
      label: getIdNumberFieldLabel,
      testId: 'id-number',
      validate: {
        props: { minLength: 8, maxLength: 9, validPattern: auPassportNumberPattern },
        messages: {
          required: 'Please enter your Australian Passport number.',
          minLength: 'Must have at least {{minLength}} characters.',
          maxLength: 'Must have at most {{maxLength}} characters.',
          invalid: 'Please provide a valid Australian passport number.',
        },
      },
    },
    {
      condition: values => values.idType === RELATED_PASSPORT || values.idType === OTHER_PASSPORT,
      component: 'Input',
      name: 'idNumber',
      label: getIdNumberFieldLabel,
      testId: 'id-number',
      validate: {
        props: { minLength: 5, maxLength: 16, validPattern: validResidencyNumberLetterPattern },
        messages: {
          required: 'Please enter your passport number.',
          minLength: 'Must have at least {{minLength}} characters.',
          maxLength: 'Must have at most {{maxLength}} characters.',
          invalid: 'Invalid passport number.',
        },
      },
    },
    {
      condition: values => values.idType === NZ_BIRTH_CERTIFICATE,
      component: 'Input',
      name: 'idNumber',
      label: getIdNumberFieldLabel,
      testId: 'id-number',
      validate: {
        props: { minLength: 10, maxLength: 10, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter your registration number.',
          minLength: 'Must have at least {{minLength}} characters.',
          maxLength: 'Must have at most {{maxLength}} characters.',
          invalid: 'Invalid registration number.',
        },
      },
    },
    {
      condition: values => values.idType === NZ_CITIZENSHIP,
      component: 'Input',
      name: 'idNumber',
      label: getIdNumberFieldLabel,
      testId: 'id-number',
      validate: {
        props: { minLength: 1, maxLength: 10, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter your certificate number.',
          minLength: 'Must have at least {{minLength}} characters.',
          maxLength: 'Must have at most {{maxLength}} characters.',
          invalid: 'Invalid certificate number.',
        },
      },
    },
    {
      condition: values =>
        values.idType === NZ_DRIVERS_LICENCE ||
        values.idType === NZ_PASSPORT ||
        values.idType === RELATED_PASSPORT ||
        values.idType === OTHER_PASSPORT,
      component: 'DatePicker',
      name: 'expiryDate',
      label: 'Expiry date',
      testId: 'expiry-date',
      day: true,
      validate: {
        props: { minDate: minExpiryDate, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter your expiry date.',
          invalid: 'Please enter a valid date.',
          completeYear: 'Please enter the complete year YYYY.',
          minDate: 'Please enter a future date.',
          dayError: 'Day must be within 1-31.',
          monthError: 'Month must be within 1-12.',
        },
        component: 'DatePicker',
      },
    },
    {
      condition: values => values.idType === OTHER_PASSPORT || values.idType === RELATED_PASSPORT,
      component: 'Select',
      name: 'issuingCountry',
      label: 'Issuing country',
      testId: 'issuing-country',
      options: values =>
        values.idType === RELATED_PASSPORT
          ? countryOptions.filter(c => c.value === 'CK' || c.value === 'TK' || c.value === 'NU')
          : countryOptions.filter(c => !['AU', 'NZ', 'CK', 'TK', 'NU'].includes(c.value)),
    },
    {
      condition: values => values.idType === OTHER_PASSPORT,
      component: 'DatePicker',
      name: 'visaStartDate',
      label: 'Visa start date',
      testId: 'visa-start-date',
      day: true,
      validate: {
        props: { minDate: minExpiryDate, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter your visa start date.',
          invalid: 'Please enter a valid date.',
          completeYear: 'Please enter the complete year YYYY.',
          minDate: 'Please enter a future date.',
          dayError: 'Day must be within 1-31.',
          monthError: 'Month must be within 1-12.',
        },
        component: 'DatePicker',
      },
    },
    {
      condition: values => values.idType === OTHER_PASSPORT,
      component: 'DatePicker',
      name: 'visaEndDate',
      label: 'Visa end date',
      testId: 'visa-end-date',
      day: true,
      optional: true,
      validate: {
        props: { minDate: minExpiryDate, validPattern: numbersOnlyPattern },
        messages: {
          required: 'Please enter your visa end date.',
          invalid: 'Please enter a valid date.',
          completeYear: 'Please enter the complete year YYYY.',
          minDate: 'Please enter a future date.',
          dayError: 'Day must be within 1-31.',
          monthError: 'Month must be within 1-12.',
        },
        component: 'DatePicker',
      },
    },
    {
      condition: values =>
        values.idType === OTHER_PASSPORT ||
        values.idType === RELATED_PASSPORT ||
        values.idType === NZ_FIREARM_LICENSE ||
        values.idType === NONE,
      component: 'CustomComponent',
      testId: 'other-id-type-warning',
      children: () => (
        <Message testId="other-id-type-warning-message" severity="info-or-minor" bg="secondary.lightBlue.t25">
          If you choose not to provide ID documentation, alternative identity verification methods may take up to 2
          business days.
        </Message>
      ),
    },
  ]);
};
